import { canEditPastStock } from './role.js'

// Stock module — Xano contract + helpers.
//
// Tables (confirmed against the Xano schema):
//   stock     { id, date, inventory_id, branch_id, N, Ncal, remark, type,
//               inventory_request, inventory_transfer_id, unique_id,
//               created_by, modified_by, Creation_Date, Modified_Date }
//   inventory { id, mainCategory, subCategory, Name, unit, everyday, min,
//               note, active, image, unique_id, Creation_Date }
//
// POST /stock contract (confirmed from the endpoint's XanoScript):
//   Inputs are a `dblink` on the stock table — i.e. every column above is a
//   FLAT top-level input — plus a separate `int stock_id?`. That is the "2
//   inputs" the Xano UI reports; there is no nested wrapper object.
//   The stack runs `db.add_or_edit` keyed on `stock_id`:
//     stock_id omitted / 0 → INSERT      stock_id = <id> → UPDATE that row
//   Server-side defaults we must NOT send: Creation_Date ("now" on insert),
//   unique_id (uuid on insert), created_by (preserved from the existing row on
//   edit). `Modified_Date` is taken straight from input — the proxy stamps it.
//   The stack also fires a realtime event on channel "SSDC" with `stock|<modified_by>`.

/* ── type ─────────────────────────────────────────────────────────────────── */
// `stock.type` is free-text and the reports filter on it (GET /stock?type=,
// GET /stock/stat?type=), so a wrong string is silently invisible to every
// report rather than an error.
//
// ⚠️ FIVE types exist, not three. Live GET /stock data for 2026-07-17 alone
// contains FOUR of them — `รับย้าย` and `แบ่งให้` are the two halves of a
// transfer, written as a +/- PAIR sharing one `inventory_transfer_id` (e.g.
// rows 91338/91337: +20 รับย้าย to branch 5, -20 แบ่งให้ from branch 8). That
// was a single day's rows, so this list is "everything observed", NOT a proven
// closed set — confirm against a wider date range before relying on it.
//
// Only the first three are written by hand. รับย้าย/แบ่งให้ belong to the
// โอนย้าย (transfer) flow and รับเพิ่ม rows carry an `inventory_request` id —
// see the writability note on STOCK_WRITE_TYPES below.
export const TYPE_CUT      = 'เบิกใช้'   // out — N ALWAYS negative
export const TYPE_RECEIVE  = 'รับเพิ่ม'  // in  — N ALWAYS positive (carries inventory_request)
export const TYPE_ADJUST   = 'ปรับ'      // correction — either sign; Stock Manager / Admin only
export const TYPE_XFER_IN  = 'รับย้าย'   // transfer in  — N positive; paired, has inventory_transfer_id
export const TYPE_XFER_OUT = 'แบ่งให้'   // transfer out — N negative; paired, has inventory_transfer_id

export const STOCK_TYPES = [TYPE_CUT, TYPE_RECEIVE, TYPE_ADJUST, TYPE_XFER_IN, TYPE_XFER_OUT]

// What this app is allowed to POST directly. The two transfer types are
// deliberately excluded: they must be created as a balanced pair by the transfer
// flow, and writing one half through here would leave stock provably wrong.
export const STOCK_WRITE_TYPES = [TYPE_CUT, TYPE_RECEIVE, TYPE_ADJUST]

// Sign rule per type. `null` = either sign allowed (ปรับ only).
// Verified against live rows: every เบิกใช้/แบ่งให้ is negative, every
// รับเพิ่ม/รับย้าย positive, and Ncal always equals N.
const TYPE_SIGN = {
  [TYPE_CUT]: -1,
  [TYPE_RECEIVE]: +1,
  [TYPE_ADJUST]: null,
  [TYPE_XFER_IN]: +1,
  [TYPE_XFER_OUT]: -1,
}

// Only Stock Manager / Admin may write a ปรับ row. Enforced again server-side in
// functions/api/stock.js — this copy exists to shape the UI, not to secure it.
export const isAdjustType = (type) => type === TYPE_ADJUST

/**
 * Apply the sign rule for a type to a user-entered quantity.
 * Callers hand us whatever the user typed; the type — not the user — decides
 * the sign, so a เบิกใช้ of 3 can never be stored as +3.
 *
 * @param type  one of STOCK_TYPES
 * @param qty   user-entered amount (ปรับ: signed; others: magnitude)
 */
export function signedN(type, qty) {
  const n = Math.trunc(Number(qty) || 0)
  const sign = TYPE_SIGN[type]
  if (sign === null) return n          // ปรับ — the user's sign is the intent
  return sign * Math.abs(n)
}

/**
 * Build a POST /stock body. Flat, per the XanoScript dblink input.
 * Omit `stock_id` to insert; pass an existing id to edit that row.
 *
 * `Ncal` mirrors `N` — same signed value (confirmed against live rows: every
 * row returned by GET /stock has Ncal === N). The Xano stack does not derive it
 * (it passes $input.Ncal straight through), so the caller owns keeping the two
 * in step. The proxy re-applies the sign to BOTH, so this stays true even if a
 * caller hands us the wrong sign.
 *
 * `stock_id` is ALWAYS sent, 0 meaning insert — never omitted. The stack does
 * `db.query where id == $input.stock_id` and then `db.add_or_edit field_value =
 * $input.stock_id`; a missing input leaves that null, and add_or_edit cannot
 * resolve add-vs-edit from null. Sending 0 matches no row, so it takes the add
 * branch cleanly.
 *
 * Not sent on purpose: Creation_Date / unique_id / created_by (server defaults),
 * Modified_Date + modified_by (stamped by the proxy from the session).
 */
export function buildStockPayload({ type, inventory_id, branch_id, date, qty, remark, stock_id }) {
  const N = signedN(type, qty)
  return {
    stock_id: Number(stock_id) || 0,   // 0 = insert
    inventory_id,
    branch_id,
    date,
    N,
    Ncal: N,
    type,
    remark: remark || '',
  }
}

// ตัด Stock — the เบิกใช้ case. `qty` is the positive amount the branch used;
// signedN flips it negative.
export function buildCutPayload({ inventory_id, branch_id, date, qty, remark }) {
  return buildStockPayload({ type: TYPE_CUT, inventory_id, branch_id, date, qty, remark })
}

/* ── inventory (the item master) ──────────────────────────────────────────── */
//
// ⚠️ GET /inventory REQUIRES `type` — it is a mode selector, not a filter, and
// sending the wrong value (or none) is a 500, not an empty list.
//
// The stack is `switch ($input.type)` over exactly four cases — "normal" (the
// paginated item list), "mainCAT" / "subCAT" / "unit" (aggregate pickers) —
// each assigning `$model`, and it has NO default case. The `// backup` block
// that handles `$input.all` is DISABLED (`!conditional`), so nothing catches an
// unmatched type: the stack reaches `response = $model` with the var never
// created and Xano fatals with `ERROR_FATAL: Missing var entry: model`.
//
// Consequences:
//   • `all=true` is DEAD — it is read only by the disabled block. There is no
//     unpaginated "give me everything" path; page through "normal" instead.
//   • `type` is overloaded: the disabled block ALSO forwards it to the
//     stock_count addon as a stock-type filter. If that block is ever
//     re-enabled, `type=normal` would leak in as a bogus filter. Check here.
export const INVENTORY_LIST = 'normal'   // paginated list + stock_count addon
export const INVENTORY_MAIN_CATEGORIES = 'mainCAT'
export const INVENTORY_SUB_CATEGORIES = 'subCAT'
export const INVENTORY_UNITS = 'unit'

const INVENTORY_PER_PAGE = 200

/**
 * Fetch the whole active item master by paging through type=normal.
 *
 * Pages rather than taking one big per_page, because the master is ~450 items
 * and climbing: a fixed cap would silently truncate the list one day, and the
 * cut page filters client-side — a missing item is invisible to search with no
 * error to notice. `totals: true` gives us pageTotal, so we follow it.
 *
 * @param get  an apiGet-shaped (path, params) => Promise
 */
export async function fetchInventoryMaster(get) {
  const first = await get('/api/inventory', { type: INVENTORY_LIST, page: 1, per_page: INVENTORY_PER_PAGE })
  let list = rows(first)
  const pageTotal = Number(first?.pageTotal) || 1
  for (let p = 2; p <= pageTotal; p++) {
    list = list.concat(rows(await get('/api/inventory', { type: INVENTORY_LIST, page: p, per_page: INVENTORY_PER_PAGE })))
  }
  return list
}

// Xano list endpoints are inconsistent — some return a bare array, some a
// paginated { items: [] } envelope. Normalize both.
export function rows(r) {
  if (Array.isArray(r)) return r
  if (Array.isArray(r?.items)) return r.items
  return []
}

// inventory.image is a Xano image field — an object with a url, not a string.
export function imageUrl(item) {
  const img = item?.image
  if (!img) return null
  if (typeof img === 'string') return img
  return img.url || img.path || null
}

export function todayISO() {
  const d = new Date()
  const p = (n) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`
}

// Which branch the ตัด Stock popup should default to.
//
// Follow the page's main branch filter when the user has one set; otherwise fall
// back to the user's own employee branch. Branch 1 is a non-stock (HQ/legacy)
// branch — it must never be a cut target, so it maps to branch 6 instead.
//
// @param filterBranchId   the history page's main branch filter (null = ทุกสาขา)
// @param employeeBranchId the logged-in user's branch_id
export function stockDefaultBranch(filterBranchId, employeeBranchId) {
  const filter = Number(filterBranchId) || 0
  if (filter > 0) return filter === 1 ? 6 : filter
  const emp = Number(employeeBranchId) || 0
  if (emp === 1) return 6
  return emp || null
}

// N days before today, as YYYY-MM-DD.
export function daysAgoISO(n) {
  const d = new Date()
  d.setDate(d.getDate() - n)
  const p = (v) => String(v).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`
}

// Pill colour per type: out-of-stock reads red, into-stock green, ปรับ amber.
// Falls back to slate so an unknown type still renders (the type list is
// "everything observed", not a proven closed set).
export function typeTone(type) {
  switch (type) {
    case TYPE_CUT: return 'bg-rose-50 text-rose-700 border-rose-200'
    case TYPE_XFER_OUT: return 'bg-orange-50 text-orange-700 border-orange-200'
    case TYPE_RECEIVE: return 'bg-emerald-50 text-emerald-700 border-emerald-200'
    case TYPE_XFER_IN: return 'bg-teal-50 text-teal-700 border-teal-200'
    case TYPE_ADJUST: return 'bg-amber-50 text-amber-800 border-amber-200'
    default: return 'bg-slate-50 text-slate-600 border-slate-200'
  }
}

// Xano timestamps are epoch ms. Time-of-day only — the row already shows a date.
export function fmtTime(ms) {
  if (!ms) return ''
  const d = new Date(Number(ms))
  if (Number.isNaN(d.getTime())) return ''
  const p = (n) => String(n).padStart(2, '0')
  return `${p(d.getHours())}:${p(d.getMinutes())}`
}

// Epoch ms → "D/M/YY HH:MM" (local). For audit lines (created / modified) where
// the exact when — not just the time — is the point.
export function fmtDateTime(ms) {
  if (!ms) return ''
  const d = new Date(Number(ms))
  if (Number.isNaN(d.getTime())) return ''
  const p = (n) => String(n).padStart(2, '0')
  return `${d.getDate()}/${d.getMonth() + 1}/${String(d.getFullYear()).slice(2)} ${p(d.getHours())}:${p(d.getMinutes())}`
}

/**
 * May this user edit/delete this stock row, and if not, why?
 *
 * @returns {{ editable: boolean, reason: null|'type'|'past' }}
 *   'type' → a transfer half (รับย้าย/แบ่งให้). Those rows only make sense as a
 *            balanced PAIR sharing an inventory_transfer_id; editing one side
 *            here would leave stock wrong, and the write proxy refuses the type
 *            anyway. The transfer flow owns them.
 *   'past'  → dated before today and the user is not Stock Manager / Admin.
 */
export function stockRowEditState(me, row) {
  if (!STOCK_WRITE_TYPES.includes(row?.type)) return { editable: false, reason: 'type' }
  if (row?.date === todayISO()) return { editable: true, reason: null }
  if (canEditPastStock(me)) return { editable: true, reason: null }
  return { editable: false, reason: 'past' }
}

export const editBlockedReason = (reason) => (
  reason === 'type' ? 'รายการโอนย้ายแก้ที่นี่ไม่ได้ — ต้องแก้เป็นคู่จากเมนูโอนย้าย'
    : reason === 'past' ? 'รายการย้อนหลังแก้ได้เฉพาะ Stock Manager หรือ Admin'
      : ''
)

// `stock_N` = the item's CURRENT level at that branch, as computed by GET /stock.
//
// ⚠️ NOT a running balance — do not label it "คงเหลือหลังรายการนี้". It is the
// same value on every row for a given inventory+branch regardless of that row's
// N. Proof from live data: rows 91328 (N=-1) and 91325 (N=+6) are both
// inventory 74 / branch 2 and BOTH report stock_N=11. A running balance would
// differ. Xano also returns `stockall.stock_N` (all branches) and
// `stock_dynamic.stock_N` — all server-derived, never stored, never sent on a write.
export const rowLevel = (r) => (r?.stock_N ?? null)

// Group an inventory list by mainCategory, preserving first-seen order.
export function byCategory(items) {
  const m = new Map()
  for (const it of items) {
    const k = it.mainCategory || 'อื่นๆ'
    if (!m.has(k)) m.set(k, [])
    m.get(k).push(it)
  }
  return m
}
